/**
 * Tournament Leaderboard Hook
 * Manages leaderboard data fetching and real-time updates.
 * Migrated from features/tournaments to make campaigns self-contained.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppStore, useIsIdle } from '../../../../store/app/appStore';
import { fetchLeaderboard } from '../../../../core/api/endpoints/tournaments.api';
import type { IPlayerScore, ITournamentSessionData } from '../../types/variants/tournament.types';
import { TOURNAMENTS_CAMPAIGN_TYPE } from '../../types/campaign.types';
import { useCampaignsStore, isTournamentSource } from '../../store/campaignsStore';
import { resolveCampaignConfig } from './useCampaignsQuery';

export interface LeaderboardEntry {
  position: number;
  playerId: string;
  playerName: string;
  score: number;
  isCurrentPlayer?: boolean;
}

interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[];
  totalActivePlayers: number;
  player?: IPlayerScore;
}

interface TournamentLeaderboardParams {
  tournamentId: string;
  sessionKey: string;
  playerId: string;
}

/**
 * Loosely-typed scoreboard row. The live API sends several id/name spellings
 * (`casinoPlayerId` / `player_id` / …); QA mocks send `IPlayerScore` rows with
 * a `selected` flag. Both flow through the same mapper below.
 */
interface RawScoreEntry {
  casinoPlayerId?: string | number;
  playerId?: string | number;
  player_id?: string | number;
  id?: string | number;
  position?: number;
  playerName?: string;
  player_name?: string;
  name?: string;
  score?: number;
  points?: number;
  selected?: boolean;
}

/**
 * Map a raw scoreboard (real API response OR a QA mock's `scoreBoard`) into the
 * `LeaderboardResponse` shape the tabs consume. Extracted so mock mode reuses
 * the exact mapping the real fetch applies.
 */
const buildLeaderboardResponse = (
  scoreBoard: RawScoreEntry[],
  joinedPlayersCount: number | undefined,
  score: IPlayerScore | undefined,
  playerId: string,
): LeaderboardResponse => {
  const leaderboard: LeaderboardEntry[] = scoreBoard.map((entry, index) => {
    const entryPlayerId = String(
      entry.casinoPlayerId ?? entry.playerId ?? entry.player_id ?? entry.id ?? `player_${index}`,
    );
    const isCurrentPlayer =
      entry.selected === true ||
      entryPlayerId === playerId ||
      entry.name === playerId ||
      entry.playerName === playerId ||
      entry.player_name === playerId;

    return {
      position: entry.position || index + 1,
      playerId: entryPlayerId,
      playerName: entry.playerName || entry.player_name || entry.name || `Player ${index + 1}`,
      score: entry.score || entry.points || 0,
      isCurrentPlayer,
    };
  });

  // Some backends omit `position` on the player score object even though the
  // current player is in the scoreboard — fall back to that entry so Position
  // updates alongside Score (mirrors RewardsTab). Otherwise Position could go
  // blank while Score updates.
  const currentPlayerPosition = leaderboard.find((entry) => entry.isCurrentPlayer)?.position;
  return {
    leaderboard,
    totalActivePlayers: joinedPlayersCount || leaderboard.length,
    player: score ? { ...score, position: score.position ?? currentPlayerPosition } : undefined,
  };
};

const fetchTournamentLeaderboard = async (params: TournamentLeaderboardParams): Promise<LeaderboardResponse> => {
  const { tournamentId, sessionKey, playerId } = params;

  const tournamentHost = useAppStore.getState().settings.tournamentHost;
  if (!tournamentHost) {
    throw new Error('Tournament host not configured');
  }

  try {
    const tournamentSessionInfo: ITournamentSessionData = {
      gameKey: useAppStore.getState().settings.gameKey,
      sessionKey,
      playerId
    };

    const data = await fetchLeaderboard({
      host: tournamentHost,
      tournamentId,
      tournamentSessionInfo
    });

    if (data.scoreBoard && Array.isArray(data.scoreBoard)) {
      return buildLeaderboardResponse(data.scoreBoard, data.joinedPlayersCount, data.score, playerId);
    }

    return {
      leaderboard: [],
      totalActivePlayers: 0
    };
  } catch (error) {
    console.error('Failed to fetch tournament leaderboard:', error);
    throw error;
  }
};

interface UseTournamentLeaderboardOptions {
  enabled?: boolean;
  refetchInterval?: number;
  staleTime?: number;
}

export const useTournamentLeaderboard = (
  tournamentId: string,
  options: UseTournamentLeaderboardOptions = {}
) => {
  // Timing/retry come from the tournaments config (single source of truth,
  // mirroring how prize pop sub-fetches are resolved). Callers may still
  // override via `options` (e.g. the card sub-fetch uses staleTime: 0).
  const { config } = resolveCampaignConfig(TOURNAMENTS_CAMPAIGN_TYPE);
  const leaderboardFetch = config.subFetches['leaderboard'];
  const timing = leaderboardFetch?.timing;
  const retry = leaderboardFetch?.retry;

  const {
    enabled = true,
    refetchInterval = timing?.refetchInterval ?? 5000,
    staleTime = timing?.staleTime ?? 30000,
  } = options;

  const sessionKey = useAppStore(state => state.settings.sessionKey);
  const playerId = useAppStore(state => state.settings.playerId);
  const isIdle = useIsIdle();

  // QA mock mode — gate the leaderboard here at the hook source so every
  // consumer (RankingTab, RewardsTab, TournamentSingleView, TournamentScoreboard)
  // is covered in one place. When mockMode is on we never touch the network;
  // the leaderboard is synthesized from the mock tournament's own `scoreBoard`.
  const mockMode = useCampaignsStore((state) => state.mockMode);
  const mockTournament = useCampaignsStore((state) => {
    if (!state.mockMode) return undefined;
    const found = state.campaigns.find((campaign) => campaign.id === tournamentId);
    return found && isTournamentSource(found) ? found : undefined;
  });

  // `!isIdle` mirrors the prize pop sub-fetch: exiting ingame (X) sets idle, which
  // must stop the leaderboard poll too — the in-iframe IntersectionObserver can't
  // see the parent hiding the lobby, so visibility alone won't gate it on exit.
  const isEnabled = enabled && !!tournamentId && !!sessionKey && !!playerId && !isIdle && !mockMode;

  const query = useQuery({
    queryKey: ['tournament-leaderboard', tournamentId, playerId],
    queryFn: () => fetchTournamentLeaderboard({
      tournamentId,
      sessionKey: sessionKey || '',
      playerId: playerId || ''
    }),
    enabled: isEnabled,
    staleTime,
    // Bind the interval to `enabled` so a disabled observer never keeps the
    // query polling. With a plain numeric interval React Query can keep ticking
    // for a query that was enabled once and then disabled (e.g. a card briefly
    // scrolled through the carousel), causing background fetches for tournaments
    // that are no longer visible.
    refetchInterval: isEnabled ? refetchInterval : false,
    refetchIntervalInBackground: timing?.refetchIntervalInBackground,
    retry: retry?.maxRetries ?? 3,
    retryDelay: (attemptIndex) =>
      Math.min((retry?.retryDelayBase ?? 1000) * 2 ** attemptIndex, retry?.maxRetryDelay ?? 10000),
    meta: {
      errorMessage: 'Failed to load tournament leaderboard'
    }
  });

  // Build the synthetic leaderboard from the mock tournament's stored scoreBoard
  // (sized by the QA "Active players" field). Resolves promptly — no spin,
  // no error — so RankingTab/RewardsTab render immediately.
  const mockData = useMemo<LeaderboardResponse | undefined>(() => {
    if (!mockMode || !mockTournament) return undefined;
    const scoreBoard = mockTournament.scoreBoard ?? [];
    // No rows (a seeded tournament the API sent without a scoreboard, or Active
    // players set to 0) → don't clobber the real/cached leaderboard with an
    // empty one; defer to the plain query below.
    if (scoreBoard.length === 0) return undefined;
    return buildLeaderboardResponse(
      scoreBoard,
      mockTournament.joinedPlayersCount,
      mockTournament.score,
      playerId ?? '',
    );
  }, [mockMode, mockTournament, playerId]);

  if (mockMode && mockData) {
    return {
      ...query,
      data: mockData,
      isLoading: false,
      isError: false,
      error: null,
    };
  }

  return query;
};
